import { UseSelectDataProps } from '@monetize/types/app';
import { ApiListResponse, DEFAULT_PAGER, Maybe } from '@monetize/types/common';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiGetAllList } from '../../../../api/axios';
import {
  getUseSelectDataByIdQueryKey,
  getUseSelectDataInfiniteQueryKey,
  getUseSelectDataQueryKey,
} from '../../../../api/queryKeysService';
import { ComposeQueryType, composeGetQuery } from '../../../../api/utils';
import { useDebounce } from '../../../../hooks/useDebounce';
import { pluralize } from '../../../../utils';
import { filteredByQuery } from './filteredByQuery';

const EMPTY_ARR = [];

/**
 * Depending on API that is called, this could be one record or a paginated list of records.
 */
function getRecordForGetById(record: unknown) {
  if (
    isObject(record) &&
    'content' in record &&
    Array.isArray(record.content) &&
    record.content.length > 0
  ) {
    return record.content[0];
  }
  if (Array.isArray(record) && record.length > 0) {
    return record[0];
  }
  return record;
}

/**
 * Combine all list data and extra fetched data into object
 */
function combineListAndByIdData({
  previousData,
  itemValue,
  endpointData,
  getByIdData,
}: {
  previousData: Record<string, any>;
  itemValue?: Maybe<string>;
  endpointData?: any[];
  getByIdData?: any[];
}): Record<string, any> {
  return [...(endpointData || []), ...(getByIdData || [])].reduce(
    (acc: Record<string, any>, item) => {
      const record = getRecordForGetById(item);
      if (record && record[itemValue as string]) {
        acc[record[itemValue as string]] = record;
      }
      return acc;
    },
    { ...previousData },
  );
}

export const useSelectData = ({
  endpoint,
  additionalSearchParams,
  internalFilterFields,
  getByIdEndpointFn,
  getByIdRecordId,
  isDisabled,
  loadAll,
  infiniteScrolling,
  itemValue,
  externalItems = EMPTY_ARR,
  itemTitle,
  query = '',
  itemSearch = '',
  omitContainsItemSearch = false,
  isSubtitleItem,
  returnItem,
  transformDataFromApi,
  filterItems,
  useRawValue,
  skipFilter,
  onFetchedItems,
}: UseSelectDataProps) => {
  const enabled = !!endpoint;
  const useInfinite = enabled && !!infiniteScrolling && !loadAll;

  // Query record function - either the limited list or paginate to fetch all items
  const apiFn = useCallback(
    (endpoint: string, queryParams: ComposeQueryType) => {
      if (loadAll) {
        const {
          currentPage: _cp,
          pageSize: _ps,
          ...filterOnlyParams
        } = queryParams;
        return apiGetAllList(endpoint, {
          filters: filterOnlyParams,
        }).then((result): ApiListResponse<any> => ({
          content: result,
          totalElements: result.length,
          totalPages: 1,
        }));
      }
      return apiGet<ApiListResponse<any>>(endpoint, {
        params: queryParams,
      }).then((response) => response.data);
    },
    [loadAll],
  );

  const debouncedSearchTerm = useDebounce(query, 500);

  const queryParams = useMemo(() => {
    const searchTerm = omitContainsItemSearch
      ? debouncedSearchTerm
      : { contains: debouncedSearchTerm };
    return composeGetQuery(DEFAULT_PAGER, {
      ...additionalSearchParams,
      [itemSearch]: searchTerm,
    });
  }, [
    itemSearch,
    debouncedSearchTerm,
    additionalSearchParams,
    omitContainsItemSearch,
  ]);

  const getByIdEndpoints = useMemo(() => {
    if (
      getByIdEndpointFn &&
      (isString(getByIdRecordId) || Array.isArray(getByIdRecordId))
    ) {
      if (Array.isArray(getByIdRecordId)) {
        return getByIdRecordId.filter(Boolean).map(getByIdEndpointFn);
      } else if (getByIdRecordId) {
        return [getByIdEndpointFn(getByIdRecordId)];
      }
    }
    return EMPTY_ARR;
  }, [getByIdRecordId, getByIdEndpointFn]);

  const applyContentTransform = useCallback(
    (response: ApiListResponse<any>): ApiListResponse<any> => ({
      ...response,
      content:
        (transformDataFromApi
          ? transformDataFromApi(response.content)
          : response.content) ?? EMPTY_ARR,
    }),
    [transformDataFromApi],
  );

  const {
    isLoading,
    isPending,
    data: endpointResponseData,
  } = useQuery({
    queryKey: getUseSelectDataQueryKey(endpoint, queryParams),
    queryFn: () => apiFn(endpoint!, queryParams).then(applyContentTransform),
    enabled: enabled && !useInfinite,
    refetchOnWindowFocus: false,
    placeholderData: (previousData, previousQuery) => previousData,
    refetchOnMount: false,
  });

  const {
    isLoading: infiniteIsLoading,
    isPending: infiniteIsPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    data: infiniteData,
  } = useInfiniteQuery({
    queryKey: getUseSelectDataInfiniteQueryKey(endpoint, queryParams),
    queryFn: ({ pageParam }) =>
      apiFn(endpoint!, { ...queryParams, currentPage: pageParam }).then(
        applyContentTransform,
      ),
    initialPageParam: DEFAULT_PAGER.page ?? 0,
    getNextPageParam: (lastPage, allPages) =>
      allPages.length < (lastPage.totalPages ?? 0)
        ? allPages.length
        : undefined,
    enabled: enabled && useInfinite,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const infiniteContent = useMemo(
    () => infiniteData?.pages.flatMap((page) => page.content) ?? EMPTY_ARR,
    [infiniteData],
  );

  const endpointData = useInfinite
    ? infiniteContent
    : endpointResponseData?.content || EMPTY_ARR;
  const totalRecordCount = useInfinite
    ? (infiniteData?.pages[0]?.totalElements ?? endpointData.length)
    : (endpointResponseData?.totalElements ?? endpointData?.length ?? 0);
  const hasMore = useInfinite
    ? !!hasNextPage
    : totalRecordCount > endpointData.length;

  /**
   * Get the record by id to make sure we can render the item in the input
   * even if we have never seen the item before (e.x. before user opens dropdown of existing form)
   */
  const {
    isPending: getByIdIsPending,
    isLoading: getByIdIsLoading,
    data: getByIdData,
  } = useQuery({
    queryKey: getUseSelectDataByIdQueryKey(getByIdRecordId),
    queryFn: () =>
      Promise.all(
        getByIdEndpoints.map((getByIdEndpoint) =>
          apiGet(getByIdEndpoint).then((res) => res.data),
        ),
      ),
    enabled: enabled && getByIdEndpoints.length > 0,
    gcTime: 1000 * 60 * 10, // 10 minutes
    staleTime: 1000 * 60 * 3, // 3 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // store every item we receive so that we can render the selected item in the input even if not in the list
  const [returnedItemCache, setReturnedItemCache] = useState<
    Record<string, any>
  >(() =>
    combineListAndByIdData({
      previousData: {},
      itemValue,
      endpointData,
      getByIdData,
    }),
  );

  /**
   * Combine all data to ensure we have every selected data point to render UI
   * Note: this was in the onSuccess callback, but since we cache aggressively this was not called
   */
  useEffect(() => {
    if (!endpointData && !getByIdData) {
      return;
    }
    setReturnedItemCache((previousData) =>
      combineListAndByIdData({
        previousData,
        itemValue,
        endpointData,
        getByIdData,
      }),
    );
  }, [endpointData, getByIdData, itemValue]);

  // If endpoint is not provided, then we will use externalItems. Otherwise, we will use endpointData
  const fetchedItems = endpoint
    ? endpointData || EMPTY_ARR
    : !skipFilter
      ? filteredByQuery(
          externalItems!,
          query,
          itemSearch,
          isSubtitleItem,
          internalFilterFields,
        ) || EMPTY_ARR
      : externalItems! || EMPTY_ARR;

  /**
   * Narrowing happens here rather than in the query function. Anything applied
   * inside `queryFn` (see `transformDataFromApi`) is baked into the React
   * Query cache under a key of endpoint + search params, so a list narrowed
   * against something mutable goes stale and is handed to every other select
   * pointed at the same endpoint.
   */
  const items = useMemo(
    () => (filterItems ? filterItems(fetchedItems) : fetchedItems),
    [filterItems, fetchedItems],
  );

  useEffect(() => {
    if (onFetchedItems && endpointData) {
      onFetchedItems(endpointData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointData]);

  const allItems = endpoint
    ? endpointData || EMPTY_ARR
    : externalItems || EMPTY_ARR;

  const getByIdLoading =
    !!getByIdEndpoints.length &&
    !!getByIdRecordId &&
    (getByIdIsPending || getByIdIsLoading);

  const mainIsPending = useInfinite ? infiniteIsPending : isPending;
  const mainIsLoading = useInfinite ? infiniteIsLoading : isLoading;
  const loading = enabled && (mainIsPending || mainIsLoading || getByIdLoading);

  const getValue = (item: any) => {
    const isObject = typeof item === 'object';
    return isObject ? item[itemValue as string] : item;
  };

  const getTitle = (item: any) => {
    const isObject = typeof item === 'object';
    return isObject ? item[itemTitle as string] : item;
  };

  const getTitleFromValue = (val: any) => {
    // handle multi-select title
    if (Array.isArray(val)) {
      val = val.filter(Boolean);
      if (val.length === 0) {
        return '';
      } else if (val.length === 1) {
        val = val[0];
      } else {
        return `${val.length} ${pluralize('item', val.length)} selected`;
      }
    }

    // Only trust the cached title when the cached record actually has this
    // select's itemTitle field. The getById cache is keyed by record id alone,
    // so a different select for the same entity (e.g. the flat AccountSelect
    // with itemTitle="accountName") can populate the same key with a record
    // that lacks our itemTitle (e.g. the hierarchy select's "name"). Falling
    // through lets allItems resolve the correct title instead of blanking.
    if (val && isString(val) && returnedItemCache[val]?.[itemTitle as string]) {
      return returnedItemCache[val][itemTitle as string];
    }

    // For returnItem mode, val is an object — check title directly or via cache by itemValue.
    // Must run before the allItems.length === 0 early return so a selected item is shown
    // even when the API list is empty (e.g. account with no contacts).
    if (val && !isString(val) && returnItem) {
      if (val[itemTitle as string]) {
        return val[itemTitle as string];
      }
      const cacheKey = val[itemValue as string];
      if (cacheKey && returnedItemCache[cacheKey]?.[itemTitle as string]) {
        return returnedItemCache[cacheKey][itemTitle as string];
      }
    }

    if (val === '' || isNil(val) || allItems.length === 0) {
      return useRawValue ? val : '';
    }

    if (returnItem) {
      // returnItem and value is already object with valid [itemTitle], then we know value is valid value
      if (val[itemTitle as string]) {
        return val[itemTitle as string];
      }

      // if not, and value only contains only itemValue, then we can find it from items.
      const foundItem = allItems.find(
        (item: any) =>
          val && item[itemValue as string] === val[itemValue as string],
      );
      if (foundItem) {
        return foundItem[itemTitle as string];
      }
      return useRawValue ? val : '';
    }

    const isObject = allItems.length > 0 && typeof allItems[0] === 'object';

    if (isObject) {
      const foundItem = allItems.find(
        (item: any) => item[itemValue as string] === val,
      );
      if (foundItem) {
        return foundItem[itemTitle as string];
      }
      return useRawValue ? val : '';
    }
    return val;
  };

  return {
    getValue,
    items,
    getTitle,
    getTitleFromValue,
    loading,
    totalRecordCount,
    hasMore,
    fetchNextPage,
    isFetchingNextPage,
    isInfiniteScrolling: useInfinite,
  };
};

export const UNIT_TEST_EXPORT = {
  combineListAndByIdData,
  getRecordForGetById,
};
